import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserCheck, Plus, Trash2, Mail } from "lucide-react";

/**
 * Admin UI for managing hiring-manager access on a specific job.
 * Mirrors PanelMembersPanel. `members` shape = [{email, name, added_at}].
 */
export default function HiringManagersPanel({
  members,
  newMember,
  setNewMember,
  showDialog,
  setShowDialog,
  onAdd,
  onRemove,
}) {
  return (
    <Card className="mt-6" data-testid="hiring-managers-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" />
              Hiring Managers
            </CardTitle>
            <CardDescription>Scoped access to this job's candidate list + CSV export (with optional PII redaction)</CardDescription>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-hiring-manager-btn">
                <Plus className="w-4 h-4 mr-2" /> Grant Access
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Grant Hiring-Manager Access</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="Jane Doe"
                    data-testid="hm-name-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="hiring.manager@example.com"
                    data-testid="hm-email-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  A personal magic-link will be emailed to this address. They'll see only this job's candidates and can export them as CSV (plain or PII-redacted).
                </p>
                <Button onClick={onAdd} className="w-full" data-testid="hm-submit-btn">
                  Grant Access &amp; Email Link
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hiring managers added yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((m) => (
              <div key={m.email} className="flex items-center justify-between p-4 border rounded-lg bg-white" data-testid={`hm-row-${m.email}`}>
                <div>
                  <h4 className="font-medium">{m.name}</h4>
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> {m.email}
                  </p>
                  {m.added_by && (
                    <Badge variant="outline" className="mt-1 text-xs">added by {m.added_by}</Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(m.email)}
                  data-testid={`hm-remove-${m.email}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
